import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  Avatar,
  AvatarGroup,
  Badge,
  Divider,
  useColorModeValue,
  IconButton,
  Flex,
  Progress,
} from '@chakra-ui/react';
import {
  ArrowLeft as ArrowLeftIcon,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  MapPin as MapPinIcon,
  Users as UsersIcon,
  Share2 as Share2Icon,
  Bookmark as BookmarkIcon,
  Heart as HeartIcon,
  MessageCircle as MessageCircleIcon,
  Book as BookIcon,
  Music as MusicIcon,
  Coffee as CoffeeIcon,
  Utensils as UtensilsIcon,
  Zap as ZapIcon,
  CheckCircle as CheckCircleIcon,
} from 'lucide-react';

interface MobileCultureEventDetailProps {
  eventId: string;
  onBack: () => void;
}

const MobileCultureEventDetail: React.FC<MobileCultureEventDetailProps> = ({ 
  eventId,
  onBack 
}) => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  
  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.500');
  const methodistBlue = '#0066CC';
  const methodistPurple = '#805AD5';
  const methodistGreen = '#38A169';
  
  // Event data mapping
  const getEventData = () => {
    switch (eventId) {
      case 'book-journal':
        return {
          title: 'Book Journal Club',
          description: 'Join our monthly book club where we discuss literature related to healthcare and wellbeing. This month we\'re reading "The Healing Self" by Deepak Chopra.',
          longDescription: 'Our Book Journal Club provides a relaxed environment for healthcare professionals to explore literature that enhances our understanding of healthcare, wellbeing, and the human experience. Each month, we select a book that offers valuable insights and perspectives relevant to our work as clinicians.\n\nThis month, we\'re reading "The Healing Self" by Deepak Chopra, which explores the connection between mind, body, and healing. The discussion will be facilitated by Dr. Emily Roberts from Behavioral Health.',
          date: 'March 15, 2025',
          time: '12:00 - 1:00 PM',
          location: 'Virtual Meeting',
          locationDetails: 'Zoom link will be sent to registered participants',
          icon: BookIcon,
          color: methodistBlue,
          participants: 18,
          maxParticipants: 25,
          organizer: 'Dr. Emily Roberts',
          department: 'Behavioral Health',
          benefits: [
            'Connect with colleagues across departments',
            'Explore literature relevant to healthcare',
            'Earn 1 CME credit',
            'Lunch provided for in-person attendees'
          ]
        };
      case 'cookoff-session':
        return {
          title: 'Healthy Cookoff Session',
          description: 'Learn quick, nutritious recipes designed for busy healthcare professionals.',
          longDescription: 'Join our nutrition team for a hands-on cooking session focused on quick, nutritious meals that fit into a busy healthcare professional\'s schedule. You\'ll learn practical cooking techniques, meal prep strategies, and recipes that can be prepared in 30 minutes or less.\n\nThis session will be led by Chef Maria Gonzalez and Dr. Sarah Thompson from our Nutrition Department. All ingredients and cooking equipment will be provided.',
          date: 'March 18, 2025',
          time: '5:30 - 7:00 PM',
          location: 'Methodist Cafeteria',
          locationDetails: 'Teaching Kitchen, Lower Level',
          icon: UtensilsIcon,
          color: methodistGreen,
          participants: 12,
          maxParticipants: 20,
          organizer: 'Chef Maria Gonzalez',
          department: 'Nutrition Services',
          benefits: [
            'Learn quick, healthy recipes',
            'Improve your nutrition knowledge',
            'Take home recipe cards and samples',
            'Connect with nutrition experts'
          ]
        };
      case 'salsa-dance':
        return {
          title: 'Salsa Dance Class',
          description: 'De-stress with our beginner-friendly salsa class. No experience needed!',
          longDescription: 'Take a break from your clinical routine and join us for a fun, energizing salsa dance class designed specifically for beginners. This class offers a perfect opportunity to move your body, release stress, and connect with colleagues in a relaxed environment.\n\nOur instructor, Carlos Mendez, has over 10 years of experience teaching dance to healthcare professionals. The class will focus on basic steps and rhythms, with no prior dance experience required.',
          date: 'March 20, 2025',
          time: '6:00 - 7:30 PM',
          location: 'Methodist Wellness Center',
          locationDetails: 'Studio B, 2nd Floor',
          icon: MusicIcon,
          color: methodistPurple,
          participants: 14,
          maxParticipants: 24,
          organizer: 'Carlos Mendez',
          department: 'Employee Wellness',
          benefits: [
            'Physical activity and stress relief',
            'No experience necessary',
            'Social connection with colleagues',
            'Comfortable clothing and shoes recommended'
          ]
        };
      case 'coffee-chat':
        return {
          title: 'Coffee & Connect',
          description: 'Informal gathering to connect with colleagues across departments.',
          longDescription: 'Coffee & Connect offers a casual environment for healthcare professionals to network and build relationships across different departments and specialties. This informal gathering provides a space to share experiences, discuss challenges, and discover collaboration opportunities.\n\nComplimentary coffee and light refreshments will be provided. Drop in any time during the event - stay for as little or as long as your schedule allows.',
          date: 'March 16, 2025',
          time: '9:00 - 10:00 AM',
          location: 'Methodist Café',
          locationDetails: 'North Wing, Main Floor',
          icon: CoffeeIcon,
          color: methodistBlue,
          participants: 8,
          maxParticipants: 15,
          organizer: 'Dr. James Wilson',
          department: 'Physician Engagement',
          benefits: [
            'Network with colleagues across specialties',
            'Complimentary coffee and refreshments',
            'Drop-in format - attend as your schedule allows',
            'Build your professional community'
          ]
        };
      case 'wellness-walk':
        return {
          title: 'Wellness Walk',
          description: 'Join a guided 30-minute walk around the hospital campus.',
          longDescription: 'Take a refreshing break with our guided Wellness Walk around the hospital campus and adjacent park. This 30-minute walk provides an opportunity to step away from clinical duties, get some fresh air, and engage in light physical activity.\n\nThe walk will be led by members of our Employee Wellness team and follows an accessible route suitable for all fitness levels. This is a perfect mid-day reset to boost your energy and clear your mind.',
          date: 'March 14, 2025',
          time: '12:15 - 12:45 PM',
          location: 'Main Entrance',
          locationDetails: 'Meet at the hospital main entrance',
          icon: HeartIcon,
          color: methodistGreen,
          participants: 22,
          maxParticipants: 30,
          organizer: 'Lisa Chen',
          department: 'Employee Wellness',
          benefits: [
            'Physical activity during your workday',
            'Fresh air and nature exposure',
            'Suitable for all fitness levels',
            'No special equipment needed'
          ]
        };
      case 'mindfulness-workshop':
        return {
          title: 'Mindfulness Workshop',
          description: 'Learn practical mindfulness techniques for the clinical environment.',
          longDescription: 'This interactive workshop introduces mindfulness practices specifically adapted for healthcare professionals in clinical settings. You\'ll learn evidence-based techniques that can be integrated into your daily routine, even during busy shifts.\n\nThe workshop will be facilitated by Dr. Michael Brown, who specializes in physician wellness and has implemented mindfulness programs in multiple healthcare systems. Participants will receive a mindfulness resource guide and access to digital meditation resources.',
          date: 'March 22, 2025',
          time: '2:00 - 3:30 PM',
          location: 'Conference Room B',
          locationDetails: 'East Wing, 3rd Floor',
          icon: ZapIcon,
          color: methodistPurple,
          participants: 15,
          maxParticipants: 25,
          organizer: 'Dr. Michael Brown',
          department: 'Physician Wellness',
          benefits: [
            'Evidence-based stress reduction techniques',
            'Practices adapted for clinical settings',
            'Take-home resources and digital access',
            'Earn 1.5 wellness credits'
          ]
        };
      default:
        return {
          title: 'Book Journal Club',
          description: 'Join our monthly book club where we discuss literature related to healthcare and wellbeing.',
          longDescription: 'Our Book Journal Club provides a relaxed environment for healthcare professionals to explore literature that enhances our understanding of healthcare, wellbeing, and the human experience.',
          date: 'March 15, 2025',
          time: '12:00 - 1:00 PM',
          location: 'Virtual Meeting',
          locationDetails: 'Zoom link will be sent to registered participants',
          icon: BookIcon,
          color: methodistBlue,
          participants: 18,
          maxParticipants: 25,
          organizer: 'Dr. Emily Roberts',
          department: 'Behavioral Health',
          benefits: [
            'Connect with colleagues across departments',
            'Explore literature relevant to healthcare',
            'Earn 1 CME credit',
            'Lunch provided for in-person attendees'
          ]
        };
    }
  };

  const eventData = getEventData();
  const participationPercentage = (eventData.participants / eventData.maxParticipants) * 100;
  
  const handleRegistration = () => {
    setIsRegistered(!isRegistered);
  };
  
  const handleSave = () => {
    setIsSaved(!isSaved);
  };

  return (
    <Box pt={12} pb={6}>
      {/* Back button */}
      <Box position="fixed" top={0} left={0} right={0} bg="white" zIndex={10} p={4} borderBottomWidth="1px" borderColor={borderColor}>
        <HStack spacing={2}>
          <IconButton
            aria-label="Back"
            icon={<Icon as={ArrowLeftIcon} />}
            variant="ghost"
            size="sm"
            onClick={onBack}
          />
          <Text fontWeight="medium">Event Details</Text>
        </HStack>
      </Box>

      {/* Event Header */}
      <Box px={4} mb={6}>
        <HStack spacing={4} mb={4}>
          <Flex
            align="center"
            justify="center"
            bg={`${eventData.color}15`}
            boxSize={16}
            borderRadius="lg"
          >
            <Icon as={eventData.icon} boxSize={8} color={eventData.color} />
          </Flex>
          <Box>
            <Text fontSize="xl" fontWeight="bold" color={textColor} mb={1}>
              {eventData.title}
            </Text>
            <HStack>
              <Badge colorScheme="blue">{eventData.department}</Badge>
              {participationPercentage > 80 && (
                <Badge colorScheme="red">Popular</Badge>
              )}
            </HStack>
          </Box>
        </HStack>

        <Text fontSize="sm" color={textColor} mb={4}>
          {eventData.description}
        </Text>

        {/* Event Details */}
        <VStack align="stretch" spacing={3} mb={6}>
          <HStack>
            <Icon as={CalendarIcon} boxSize={5} color={methodistBlue} mr={3} />
            <Box>
              <Text fontSize="sm" fontWeight="medium">{eventData.date}</Text>
              <Text fontSize="xs" color={secondaryTextColor}>Date</Text>
            </Box>
          </HStack>
          <HStack>
            <Icon as={ClockIcon} boxSize={5} color={methodistBlue} mr={3} />
            <Box>
              <Text fontSize="sm" fontWeight="medium">{eventData.time}</Text>
              <Text fontSize="xs" color={secondaryTextColor}>Time</Text>
            </Box>
          </HStack>
          <HStack>
            <Icon as={MapPinIcon} boxSize={5} color={methodistBlue} mr={3} />
            <Box>
              <Text fontSize="sm" fontWeight="medium">{eventData.location}</Text>
              <Text fontSize="xs" color={secondaryTextColor}>{eventData.locationDetails}</Text>
            </Box>
          </HStack>
          <HStack>
            <Icon as={UsersIcon} boxSize={5} color={methodistBlue} mr={3} />
            <Box>
              <Text fontSize="sm" fontWeight="medium">{eventData.organizer}</Text>
              <Text fontSize="xs" color={secondaryTextColor}>Organizer</Text>
            </Box>
          </HStack>
        </VStack>

        {/* Participation */}
        <Box mb={6}>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="sm" fontWeight="medium">Participation</Text>
            <Text fontSize="sm" color={secondaryTextColor}>
              {eventData.participants}/{eventData.maxParticipants}
            </Text>
          </HStack>
          <Progress 
            value={participationPercentage} 
            size="sm" 
            colorScheme="blue" 
            borderRadius="full" 
            mb={2}
          />
          <HStack spacing={1}>
            <AvatarGroup size="xs" max={5}>
              {[...Array(5)].map((_, i) => (
                <Avatar key={i} name={`Person ${i}`} bg={`${['blue', 'green', 'purple', 'pink', 'orange'][i % 5]}.500`} />
              ))}
            </AvatarGroup>
            {eventData.participants > 5 && (
              <Text fontSize="xs" color={secondaryTextColor}>
                +{eventData.participants - 5} more
              </Text>
            )}
          </HStack>
        </Box>

        {/* About */}
        <Box mb={6}>
          <Text fontSize="md" fontWeight="medium" mb={2}>About This Event</Text>
          <Text fontSize="sm" color={textColor} whiteSpace="pre-line">
            {eventData.longDescription}
          </Text>
        </Box>

        {/* Benefits */}
        <Box mb={6}>
          <Text fontSize="md" fontWeight="medium" mb={2}>Benefits</Text>
          <VStack align="stretch" spacing={2}>
            {eventData.benefits.map((benefit, index) => (
              <HStack key={index} spacing={2}>
                <Icon as={CheckCircleIcon} color={methodistGreen} boxSize={4} />
                <Text fontSize="sm">{benefit}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        <Divider my={6} />

        {/* Action Buttons */}
        <VStack spacing={3} width="100%">
          <Button
            colorScheme={isRegistered ? "green" : "blue"}
            width="100%"
            leftIcon={<Icon as={isRegistered ? CheckCircleIcon : CalendarIcon} />}
            onClick={handleRegistration}
          >
            {isRegistered ? "Registered" : "Register for Event"}
          </Button>
          
          <HStack width="100%" spacing={3}>
            <Button
              variant="outline"
              width="50%"
              leftIcon={<Icon as={BookmarkIcon} />}
              onClick={handleSave}
              colorScheme={isSaved ? "blue" : "gray"}
            >
              {isSaved ? "Saved" : "Save"}
            </Button>
            <Button
              variant="outline"
              width="50%"
              leftIcon={<Icon as={Share2Icon} />}
              colorScheme="gray"
            >
              Share
            </Button>
          </HStack>
        </VStack>

        {/* Discussion */}
        <Box mt={6} p={4} bg="gray.50" borderRadius="lg">
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="medium">Event Discussion</Text>
            <Badge colorScheme="blue">8 comments</Badge>
          </HStack>
          <Text fontSize="xs" color={secondaryTextColor} mb={3}>
            Connect with other participants
          </Text>
          <Button
            size="sm"
            width="100%"
            leftIcon={<Icon as={MessageCircleIcon} />}
            variant="outline"
            colorScheme="blue"
          >
            View Discussion
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileCultureEventDetail;
